<template>
<section v-if="shouldRender" id="features" class="relative overflow-hidden bg-gradient-to-b from-white via-white to-slate-25">
  <div class="absolute inset-x-0 top-0 -z-10 h-72 bg-hilab/5 blur-[140px]"></div>
  <div class="container-full-width section-padding">
    <div class="mx-auto max-w-3xl text-center">
      <span v-if="sectionBadge" class="badge">{{ sectionBadge }}</span>
      <h2 v-if="sectionTitle" class="section-title mt-6">{{ sectionTitle }}</h2>
      <p v-if="sectionSubtitle" class="section-subtitle mx-auto">
        {{ sectionSubtitle }}
      </p>
    </div>
      <div v-if="heroFeatures.length" class="mt-14 grid gap-6 md:grid-cols-2">
        <article
          v-for="feature in heroFeatures"
          :key="feature.title"
          class="feature-card card-surface gradient-border group relative overflow-hidden px-8 pb-8 pt-10 scroll-fade-in"
        >
          <span class="icon-circle mb-4 h-12 w-12 bg-hilab/10 text-hilab">
            <LucideIcon :name="resolveIcon(feature.icon)" :size="24" class="feature-icon text-hilab" />
          </span>
          <h3 class="text-xl font-semibold text-slate-900">{{ feature.title }}</h3>
          <p class="mt-4 text-base leading-relaxed text-slate-500">{{ feature.description }}</p>
          <ul class="mt-6 space-y-3 text-sm text-slate-500">
            <li v-for="item in feature.points" :key="item" class="flex items-start gap-2">
              <LucideIcon name="check" :size="16" class="mt-0.5 text-hilab shrink-0" />
              {{ item }}
            </li>
          </ul>
          <div class="pointer-events-none absolute -bottom-20 right-0 h-40 w-40 rounded-full bg-hilab/10 blur-3xl transition group-hover:translate-y-3"></div>
        </article>
      </div>
      <div v-if="secondaryFeatures.length" class="mt-16 rounded-3xl border border-slate-100 bg-white/70 p-8 shadow-sm">
        <h3 class="text-lg font-semibold text-slate-900">Các tính năng khác</h3>
        <div class="mt-6 grid gap-4 md:grid-cols-3">
            <div v-for="feature in secondaryFeatures" :key="feature" class="flex items-center gap-3 rounded-2xl bg-slate-25 px-4 py-3 text-sm font-medium text-slate-500">
              <LucideIcon name="check-check" :size="16" class="text-hilab shrink-0" />
              {{ feature }}
            </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import LucideIcon from '../../dashboard/components/LucideIcon.vue';

const iconMap = {
  automation: 'brain-cog',
  omnichannel: 'message-circle',
  ai: 'bot',
  data: 'database',
  integration: 'zap',
  analytics: 'chart-spline',
  support: 'life-buoy',
  automation_workflow: 'refresh-cw',
  multi_channel: 'globe',
  ai_chatbot: 'bot-message-square',
  data_collection: 'file-plus',
  google_sheets: 'file',
  lark_sync: 'refresh-ccw',
  api: 'code',
  webhook: 'zap',
  help_center: 'book-open',
  sla: 'clock',
  reports: 'chart-no-axes-column',
  team: 'users',
  security: 'key-square',
  voice: 'volume2',
  email: 'mail',
  sms: 'message-square',
  whatsapp: 'message-circle',
  facebook: 'message-square',
  instagram: 'message-circle',
  telegram: 'message-circle',
  line: 'message-circle',
  zalo: 'message-circle',
  website: 'globe',
  campaign: 'megaphone',
  macro: 'repeat',
  label: 'tag',
  custom_attribute: 'settings',
  canned_response: 'book',
  bulk_action: 'upload',
  contact_export: 'download',
  conversation_summary: 'file-clock',
  priority_alert: 'triangle-alert',
  business_faq: 'circle-question-mark',
  business_data: 'database',
  image_library: 'library',
  linear: 'code',
  webhook_api: 'zap',
  embedding_search: 'search',
  insert_article: 'book-open',
  voice_recorder: 'volume2',
  ip_lookup: 'globe',
  spam_filter: 'shield',
};

const props = defineProps({
  content: {
    type: Object,
    default: () => ({}),
  },
});

const toArray = value => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

const sectionContent = computed(() => props.content || {});
const heroFeatures = computed(() => toArray(sectionContent.value.primary).filter(feature => feature?.title));
const secondaryFeatures = computed(() => toArray(sectionContent.value.secondary).filter(Boolean));
const sectionBadge = computed(() => sectionContent.value.badge || '');
const sectionTitle = computed(() => sectionContent.value.title || '');
const sectionSubtitle = computed(() => sectionContent.value.subtitle || '');
const shouldRender = computed(() => heroFeatures.value.length || secondaryFeatures.value.length);

const resolveIcon = iconKey => iconMap[iconKey] || 'check-check';
</script>
